import React, { useState } from 'react';
import { Deal } from '../types/pipeline';
import { TEAM_MEMBERS } from '../data/mockPipelineData';
import { X, DollarSign, Home, Calendar, TrendingUp, User, Phone, Mail, MapPin, Clock, MessageSquare, FileText, ArrowRight, CheckCircle2, ChevronLeft } from 'lucide-react';
interface LoanDetailModalProps {
  deal: Deal;
  onClose: () => void;
  onBack?: () => void;
}
export function LoanDetailModal({
  deal,
  onClose,
  onBack
}: LoanDetailModalProps) {
  const [newNote, setNewNote] = useState('');
  const loanOfficer = TEAM_MEMBERS.find(m => m.id === deal.assignedTo);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(date);
  };
  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'Pre-qual':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Application':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'Processing':
        return 'bg-violet-100 text-violet-700 border-violet-200';
      case 'Underwriting':
        return 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200';
      case 'Clear to Close':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };
  const getNoteIcon = (type: string) => {
    switch (type) {
      case 'status_change':
        return <ArrowRight className="w-4 h-4" data-id="element-87" />;
      case 'document':
        return <FileText className="w-4 h-4" data-id="element-88" />;
      case 'communication':
        return <Phone className="w-4 h-4" data-id="element-89" />;
      default:
        return <MessageSquare className="w-4 h-4" data-id="element-90" />;
    }
  };
  const getNoteColor = (type: string) => {
    switch (type) {
      case 'status_change':
        return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'document':
        return 'bg-purple-50 border-purple-200 text-purple-700';
      case 'communication':
        return 'bg-green-50 border-green-200 text-green-700';
      default:
        return 'bg-slate-50 border-slate-200 text-slate-700';
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-91">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200" data-id="element-92">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-5 flex items-start justify-between" data-id="element-93">
          <div className="flex-1 min-w-0" data-id="element-94">
            <div className="flex items-center space-x-3 mb-2" data-id="element-95">
              <h2 className="text-2xl font-bold truncate" data-id="element-96">
                {deal.borrowerName}
              </h2>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStageColor(deal.stage)} bg-white/95 flex-shrink-0`} data-id="element-97">
                {deal.stage}
              </span>
            </div>
            <p className="text-blue-100 text-sm" data-id="element-98">
              Loan Application #{deal.id.toUpperCase()}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0 ml-4" aria-label="Close modal" data-id="element-99">
            <X className="w-5 h-5" data-id="element-100" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-101">
          <div className="p-6 space-y-6" data-id="element-102">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-103">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-104">
                <div className="flex items-center text-slate-500 text-xs font-medium mb-2" data-id="element-105">
                  <DollarSign className="w-4 h-4 mr-1.5" data-id="element-106" />
                  Loan Amount
                </div>
                <div className="text-xl font-bold text-slate-900" data-id="element-107">
                  {formatCurrency(deal.amount)}
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-108">
                <div className="flex items-center text-slate-500 text-xs font-medium mb-2" data-id="element-109">
                  <TrendingUp className="w-4 h-4 mr-1.5" data-id="element-110" />
                  Probability
                </div>
                <div className="text-xl font-bold text-slate-900" data-id="element-111">
                  {deal.probability}%
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-112">
                <div className="flex items-center text-slate-500 text-xs font-medium mb-2" data-id="element-113">
                  <Clock className="w-4 h-4 mr-1.5" data-id="element-114" />
                  Days in Stage
                </div>
                <div className="text-xl font-bold text-slate-900" data-id="element-115">
                  {deal.daysInStage}
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-116">
                <div className="flex items-center text-slate-500 text-xs font-medium mb-2" data-id="element-117">
                  <Calendar className="w-4 h-4 mr-1.5" data-id="element-118" />
                  Est. Close
                </div>
                <div className="text-sm font-bold text-slate-900" data-id="element-119">
                  {formatDate(deal.estimatedCloseDate)}
                </div>
              </div>
            </div>

            {/* Borrower & Property Info */}
            <div className="grid md:grid-cols-2 gap-6" data-id="element-120">
              {/* Borrower Details */}
              <div className="bg-white border border-slate-200 rounded-lg p-5" data-id="element-121">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center text-base" data-id="element-122">
                  <User className="w-4 h-4 mr-2 text-blue-600" data-id="element-123" />
                  Borrower Information
                </h3>
                <div className="space-y-3" data-id="element-124">
                  <div className="flex items-start" data-id="element-125">
                    <Mail className="w-4 h-4 mr-3 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-126" />
                    <div className="min-w-0 flex-1" data-id="element-127">
                      <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-128">
                        Email
                      </div>
                      <div className="text-sm text-slate-900 truncate" data-id="element-129">
                        {deal.borrowerEmail || 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-130">
                    <Phone className="w-4 h-4 mr-3 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-131" />
                    <div className="min-w-0 flex-1" data-id="element-132">
                      <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-133">
                        Phone
                      </div>
                      <div className="text-sm text-slate-900" data-id="element-134">
                        {deal.borrowerPhone || 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-135">
                    <User className="w-4 h-4 mr-3 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-136" />
                    <div className="min-w-0 flex-1" data-id="element-137">
                      <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-138">
                        Loan Officer
                      </div>
                      <div className="text-sm text-slate-900 font-medium" data-id="element-139">
                        {loanOfficer?.name || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Property Details */}
              <div className="bg-white border border-slate-200 rounded-lg p-5" data-id="element-140">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center text-base" data-id="element-141">
                  <Home className="w-4 h-4 mr-2 text-blue-600" data-id="element-142" />
                  Property Details
                </h3>
                <div className="space-y-3" data-id="element-143">
                  <div className="flex items-start" data-id="element-144">
                    <MapPin className="w-4 h-4 mr-3 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-145" />
                    <div className="min-w-0 flex-1" data-id="element-146">
                      <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-147">
                        Address
                      </div>
                      <div className="text-sm text-slate-900" data-id="element-148">
                        {deal.propertyAddress || 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-149">
                    <Home className="w-4 h-4 mr-3 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-150" />
                    <div className="min-w-0 flex-1" data-id="element-151">
                      <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-152">
                        Property Type
                      </div>
                      <div className="text-sm text-slate-900" data-id="element-153">
                        {deal.propertyType}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-154">
                    <FileText className="w-4 h-4 mr-3 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-155" />
                    <div className="min-w-0 flex-1" data-id="element-156">
                      <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-157">
                        Loan Type
                      </div>
                      <div className="text-sm text-slate-900" data-id="element-158">
                        {deal.loanType}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity / Notes */}
            <div className="bg-white border border-slate-200 rounded-lg p-5" data-id="element-159">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center text-base" data-id="element-160">
                <MessageSquare className="w-4 h-4 mr-2 text-blue-600" data-id="element-161" />
                Recent Activity
              </h3>

              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto" data-id="element-162">
                {deal.notes && deal.notes.length > 0 ? deal.notes.map(note => <div key={note.id} className={`flex space-x-3 p-3 rounded-lg border ${getNoteColor(note.type)}`} data-id="element-163">
                      <div className="flex-shrink-0" data-id="element-164">
                        <img src={note.authorAvatar} alt={note.author} className="w-8 h-8 rounded-full border-2 border-white shadow-sm" data-id="element-165" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-166">
                        <div className="flex items-center justify-between mb-1" data-id="element-167">
                          <span className="text-sm font-semibold text-slate-900" data-id="element-168">
                            {note.author}
                          </span>
                          <span className="text-xs text-slate-500 flex-shrink-0 ml-2" data-id="element-169">
                            {formatTimestamp(note.timestamp)}
                          </span>
                        </div>
                        <div className="flex items-start space-x-2" data-id="element-170">
                          <div className="flex-shrink-0 mt-0.5" data-id="element-171">
                            {getNoteIcon(note.type)}
                          </div>
                          <p className="text-sm text-slate-700 leading-relaxed" data-id="element-172">
                            {note.content}
                          </p>
                        </div>
                      </div>
                    </div>) : <p className="text-sm text-slate-500 text-center py-8" data-id="element-173">
                    No activity yet
                  </p>}
              </div>

              {/* Add Note */}
              <div className="border-t border-slate-200 pt-4" data-id="element-174">
                <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note or update..." className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-shadow" rows={3} data-id="element-175" />
                <div className="flex justify-end mt-3" data-id="element-176">
                  <button onClick={() => {
                  setNewNote('');
                }} disabled={!newNote.trim()} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-slate-300 disabled:cursor-not-allowed transition-all" data-id="element-177">
                    Add Note
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-slate-200 bg-slate-50 px-6 py-4 flex justify-between items-center" data-id="element-178">
          <div className="flex items-center space-x-3" data-id="element-179">
            {onBack && <button onClick={onBack} className="px-5 py-2.5 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors text-sm font-medium focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 flex items-center" data-id="element-180">
                <ChevronLeft className="w-4 h-4 mr-1.5" data-id="element-181" />
                Back to List
              </button>}
            <button onClick={onClose} className="px-5 py-2.5 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors text-sm font-medium focus:ring-2 focus:ring-slate-500 focus:ring-offset-2" data-id="element-182">
              Close
            </button>
          </div>
          <div className="flex space-x-3" data-id="element-183">
            <button className="px-5 py-2.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-sm font-medium focus:ring-2 focus:ring-slate-500 focus:ring-offset-2" data-id="element-184">
              View in Salesforce
            </button>
            <button className="px-5 py-2.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors text-sm font-medium flex items-center focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-185">
              <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-186" />
              Move to Next Stage
            </button>
          </div>
        </div>
      </div>
    </div>;
}